import { Button, FormControlLabel, Popover, Stack, Switch, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH, isValidColumnWidth } from "../utils/tabularColumnWidths.ts";

interface Props {
  width: number;
  autosize: boolean;
  anchorEl: HTMLElement;
  onClose: () => void;
  onApply: (width: number | undefined, applyToAll: boolean) => void;
}

const ColumnWidthPopover = ({ width, autosize, anchorEl, onClose, onApply }: Props) => {
  const [applyToAll, setApplyToAll] = useState(false);
  const [useAutosize, setUseAutosize] = useState(autosize);
  const [value, setValue] = useState(String(Math.round(width)));
  const valid = useAutosize || isValidColumnWidth(value);

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { role: "dialog", "aria-label": "Column width" } }}
    >
      <Stack
        component="form"
        spacing={2}
        sx={{ p: 2, width: 281, maxWidth: "100%" }}
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) {
            onApply(useAutosize ? undefined : Number(value), applyToAll);
          }
        }}
      >
        <Typography variant="subtitle2">Column width</Typography>
        <FormControlLabel
          label="Autosize"
          control={<Switch checked={useAutosize} onChange={(_, checked) => setUseAutosize(checked)} />}
        />
        <TextField
          autoFocus={!useAutosize}
          disabled={useAutosize}
          fullWidth
          label="Width (pixels)"
          type="number"
          value={value}
          onFocus={(event) => event.target.select()}
          onChange={(event) => setValue(event.target.value)}
          error={!valid}
          helperText={`Enter a whole number, ${MIN_COLUMN_WIDTH}–${MAX_COLUMN_WIDTH} pixels.`}
          slotProps={{ htmlInput: { min: MIN_COLUMN_WIDTH, max: MAX_COLUMN_WIDTH, step: 1 } }}
        />
        <FormControlLabel
          label="Apply to all columns"
          control={<Switch checked={applyToAll} onChange={(_, checked) => setApplyToAll(checked)} />}
        />
        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!valid}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};

export default ColumnWidthPopover;
